import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type AdminSection =
  | "dashboard"
  | "waiting_on"
  | "knowledge_vault"
  | "classes"
  | "registrations"
  | "instructors"
  | "payroll"
  | "popup"
  | "open_house"
  | "reports"
  | "archived_alerts"
  | "settings";

export interface AdminActivityEvent {
  id: string;
  section: AdminSection;
  activity_type: string;
  source_table: string | null;
  source_record_id: string | null;
  title: string;
  summary: string | null;
  status: "unreviewed" | "reviewed";
  created_at: string;
}

type Counts = Record<AdminSection, number>;

const EMPTY: Counts = {
  dashboard: 0,
  waiting_on: 0,
  knowledge_vault: 0,
  classes: 0,
  registrations: 0,
  instructors: 0,
  payroll: 0,
  popup: 0,
  open_house: 0,
  reports: 0,
  archived_alerts: 0,
  settings: 0,
};

/**
 * Fetches unreviewed activity counts per admin section and subscribes to
 * realtime changes so the sidebar badges refresh without a page reload.
 */
export function useAdminActivityBadges(enabled: boolean = true) {
  const [counts, setCounts] = useState<Counts>(EMPTY);
  const [events, setEvents] = useState<AdminActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const recount = useCallback((rows: AdminActivityEvent[]) => {
    const next = { ...EMPTY };
    for (const r of rows) {
      if (r.status !== "unreviewed") continue;
      const s = r.section as AdminSection;
      if (s in next) next[s] += 1;
    }
    setCounts(next);
  }, []);

  const fetchAll = useCallback(async () => {
    const { data, error } = await supabase
      .from("admin_activity_events" as any)
      .select("id, section, activity_type, source_table, source_record_id, title, summary, status, created_at")
      .eq("status", "unreviewed")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) {
      setLoading(false);
      return;
    }
    const rows = (data ?? []) as unknown as AdminActivityEvent[];
    setEvents(rows);
    recount(rows);
    setLoading(false);
  }, [recount]);

  useEffect(() => {
    if (!enabled) return;
    fetchAll();
    const channel = supabase
      .channel(`admin-activity-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "admin_activity_events" },
        () => {
          fetchAll();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, fetchAll]);

  const markSectionReviewed = useCallback(
    async (section: AdminSection) => {
      const { error } = await (supabase.rpc as any)("mark_admin_activity_reviewed", {
        _section: section,
        _ids: null,
      });
      if (!error) {
        setEvents((prev) => prev.filter((e) => e.section !== section));
        setCounts((prev) => ({ ...prev, [section]: 0 }));
      }
    },
    [],
  );

  const markItemsReviewed = useCallback(async (ids: string[]) => {
    if (!ids.length) return;
    const { error } = await (supabase.rpc as any)("mark_admin_activity_reviewed", {
      _section: null,
      _ids: ids,
    });
    if (!error) {
      setEvents((prev) => {
        const next = prev.filter((e) => !ids.includes(e.id));
        recount(next);
        return next;
      });
    }
  }, [recount]);

  return {
    counts,
    events,
    loading,
    refresh: fetchAll,
    markSectionReviewed,
    markItemsReviewed,
  };
}
